import { useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import FilterSearch from "../../components/Filtering/FilterSearch";
import SearchButton from "../../components/buttons/SearchButton";
import FilterRightBox from "../../components/Filtering/FilterRightBoxOffering";
import DataTable from "../../components/DataTable/DataTable";
import CustomButton from "../../components/buttons/CustomButton";
import WizardNav from "../../components/Wizard/WizardNav";
import AddIcon from "@mui/icons-material/Add";

import { useAuthRoles } from "../../hooks/useAuthRoles";
import { useFilteredData } from "../../hooks/useFilteredData";
import { useTableControls } from "../../hooks/useTableControls";
import { useWorkerApiHelper } from "../../hooks/useWorkerApiHelper";

import { Spinner } from "../../components/Filtering/Reusable/Spinner";
import { getTextByLang } from "../../utils/formUtils";
import { formatDate } from "../../utils/constants";

import { useGetDatasets } from "../../hooks/datasets/useGetDataset";
import { useDeleteDataset } from "../../hooks/datasets/useDeleteDataset";
import { extractIdFromIri } from "../../utils/extractFromIri";

export default function Dataset({
  stepData,
  setStepData,
  next,
  back,
  canGoNext,
  isFirst,
  isLast,
}) {
  const { callApiWithWorker } = useWorkerApiHelper();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const {
    searchTerm,
    pendingSearchTerm,
    filters,
    open,
    pagination,
    rowCount,
    orgProvider,
    setPagination,
    setRowCount,
    setTableData,
    handleSearch,
    handleClearSearch,
    handleSearchSubmit,
    handleSearchKeyDown,
    handleClose,
    handleApplyFilters,
  } = useTableControls([]);

  const { isProvider } = useAuthRoles();

  const handleSelectedRowsChange = useCallback(
    (selectedRowsOrSelection, maybeRowSelection) => {
      let rowSelection = maybeRowSelection;
      let selectedRows = [];

      if (Array.isArray(selectedRowsOrSelection)) {
        selectedRows = selectedRowsOrSelection;
        rowSelection = maybeRowSelection;
      } else if (
        selectedRowsOrSelection &&
        typeof selectedRowsOrSelection === "object"
      ) {
        rowSelection = selectedRowsOrSelection;
      }
      let selectedIris = Object.keys(rowSelection || {}).filter(
        (k) => rowSelection[k],
      );
      if (selectedIris.length === 0 && selectedRows.length > 0) {
        selectedIris = selectedRows
          .map((r) => r?.original ?? r)
          .map((r) => r?.id)
          .filter(Boolean)
          .map(String);
      }

      setStepData("step3", {
        selectedIris,
        rowSelection: rowSelection || {},
        selectedDatasets: selectedRows,
      });
    },
    [setStepData],
  );

  const {
    data: pageResponse,
    isLoading,
    isFetching,
  } = useGetDatasets({
    callApiWithWorker,
    language: i18n.language,
    pageIndex: pagination.pageIndex,
    pageSize: pagination.pageSize,
  });

  const rows = useMemo(() => {
    const content = pageResponse?.content ?? [];
    return content.map((item) => ({
      ...item,
      titleText: getTextByLang(item.title, i18n.language),
      descriptionText: getTextByLang(item.description, i18n.language),
      rowId: item.identifier || item.id,
    }));
  }, [pageResponse, i18n.language]);

  useEffect(() => {
    setRowCount(pageResponse?.totalElements ?? 0);
  }, [pageResponse?.totalElements, setRowCount]);

  useEffect(() => {
    setTableData(rows);
  }, [rows, setTableData]);

  const deleteMutation = useDeleteDataset({ callApiWithWorker, t });

  const handleEdit = (rowLike) => {
    const record = rowLike?.original ?? rowLike;

    navigate(`/editdataoffering/${extractIdFromIri(record.id)}`, {
      state: { record },
    });
  };

  const handleNameClick = (rowLike) => {
    const record = rowLike?.original ?? rowLike;

    navigate(`/dataofferingdetail/${extractIdFromIri(record.id)}`, {
      state: { record },
    });
  };

  const handleDelete = async (rowLike) => {
    const record = rowLike?.original ?? rowLike;
    const datasetId =
      extractIdFromIri(record?.id) || record?.id || record?.identifier;

    try {
      await deleteMutation.mutateAsync({ id: datasetId });
    } catch (e) {
      void e;
    }
  };

  const selectedIris = Array.isArray(stepData?.selectedIris)
    ? stepData.selectedIris
    : [];

  const isSelectionValid = selectedIris.length > 0;
  const canProceed = Boolean(canGoNext && isSelectionValid);

  const columns = useMemo(
    () => [
      {
        header: t("dataOfferings.offerings.dict.lastUpdate"),
        accessorKey: "updated_at",
        meta: { width: "20%" },
        cell: ({ getValue, row }) =>
          formatDate(getValue() ?? row.original.modified),
      },
      {
        header: t("dataOfferings.offerings.dict.name"),
        accessorKey: "titleText",
        meta: { width: "40%" },
        cell: ({ getValue }) => (
          <div className="centered-cell">
            <span className="bold">{getValue()}</span>
          </div>
        ),
      },
      {
        header: t("dataOfferings.offerings.dict.description"),
        accessorKey: "descriptionText",
        meta: { width: "40%" },
      },
    ],
    [t],
  );

  const filteredData = useFilteredData(rows, searchTerm, filters, columns);

  return (
    <section className="pb4">
      <div className="labeltittle">
        <p className="tittlecatalog--embedded">
          {t("wizard.steps.dataset.selection")}
          <span className="required">*</span>
        </p>
        {isProvider && (
          <CustomButton
            onClick={() => navigate("/createdataoffering")}
            startIcon={<AddIcon />}
          >
            {t("wizard.steps.dataset.create")}
          </CustomButton>
        )}
      </div>

      <div className="w86 filteractions pt3">
        <FilterSearch
          value={pendingSearchTerm}
          onChange={handleSearch}
          onClear={handleClearSearch}
          onKeyDown={handleSearchKeyDown}
        />
        <div className="plr2">
          <SearchButton onClick={handleSearchSubmit} />
        </div>
      </div>

      <FilterRightBox
        open={open}
        onClose={handleClose}
        onApplyFilters={handleApplyFilters}
      />

      <div className="bgwhite mtb2">
        <div className="plr3 ptb">
          <DataTable
            data={filteredData}
            columns={columns}
            onEdit={(row) => handleEdit(row)}
            onDelete={(row) => handleDelete(row)}
            onRowClick={handleNameClick}
            showActions={orgProvider}
            initialSorting={[{ id: "updated_at", desc: true }]}
            enableRowSelection
            selectionMode="multi"
            getRowId={(row) => row.id}
            selectedRowIds={stepData?.rowSelection || {}}
            onSelectedRowsChange={handleSelectedRowsChange}
            pagination={pagination}
            setPagination={setPagination}
            rowCount={rowCount}
          />
        </div>
      </div>

      <div className="wz-footer">
        <WizardNav
          back={back}
          next={next}
          isFirst={isFirst}
          canGoNext={canProceed}
        />
      </div>

      {(isLoading || isFetching || deleteMutation.isPending) && (
        <div className="loading">
          <Spinner size="large" color="var(--main-color)" />
        </div>
      )}
    </section>
  );
}
